import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useFetch from "hooks/useFetch";
import { RoutePaths } from "enums/RoutePaths";
import {
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  Table,
} from "stories/Admin";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import styles from "./OverviewPage.module.scss";

/*
 * Superadmin: tall for hele iBønn på ett sted. Alt kommer fra GET api/v1/admin/overview;
 * detaljene ligger på sidene det lenkes til.
 */

interface CountBy {
  label: string;
  count: number;
}

interface TopApiKey {
  id: number;
  name: string;
  mosqueName: string | null;
  orgNr: string;
  requests30Days: number;
  lastUsedAt: string | null;
}

export interface AdminOverview {
  mosques: { total: number; awaitingApproval: number; mosqueAdmins: number };
  app: {
    activeDevices: number;
    byPlatform: CountBy[];
    mosqueSubscriptions: number;
    /** Installasjoner sett siste 30 dager per plattform og appversjon, nyeste først. */
    appVersions?: { platform: string; total: number; versions: CountBy[] }[];
  };
  janazah: {
    followers: number;
    followersByPlatform: CountBy[];
    upcoming: number;
    noticesLast30Days: number;
  };
  apiKeys: {
    total: number;
    active: number;
    revoked: number;
    usedLast7Days: number;
    mosques: number;
    requestsTotal: number;
    requestsLast30Days: number;
    top: TopApiKey[];
  };
  tv: {
    total: number;
    active: number;
    online: number;
    pending: number;
    expired: number;
    revoked: number;
    mosques: number;
    byPlatform: CountBy[];
    byAppVersion: CountBy[];
  };
  pushLast30Days: number;
  generatedAt: string;
}

const nf = (n: number) => n.toLocaleString("nb-NO");

const platformLabel = (p: string) =>
  ({
    android: "Android",
    ios: "iOS",
    "android-tv": "Android TV",
    tvos: "Apple TV",
    "tizen-tv": "Samsung TV",
    ukjent: "Ukjent",
  })[p.toLowerCase()] ?? p;

const formatDateTime = (iso: string | null) => {
  if (!iso) return "Aldri";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleString("nb-NO", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Stat: React.FC<{
  label: string;
  value: number | undefined;
  hint?: React.ReactNode;
  strong?: boolean;
}> = ({ label, value, hint, strong }) => (
  <div className={strong ? `${styles.stat} ${styles.strong}` : styles.stat}>
    <b>{value === undefined ? "–" : nf(value)}</b>
    <span>{label}</span>
    {hint && <small>{hint}</small>}
  </div>
);

const Split: React.FC<{ items: CountBy[]; format?: (s: string) => string }> = ({
  items,
  format = platformLabel,
}) =>
  items.length ? (
    <div className={styles.split}>
      {items.map((i) => (
        <Badge key={i.label} tone="grey">
          {format(i.label)}: {nf(i.count)}
        </Badge>
      ))}
    </div>
  ) : null;

const OverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { execute } = useFetch<AdminOverview>();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [data, setData] = useState<AdminOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = await executeRef.current("/api/v1/admin/overview", "GET");
    if (result) setData(result);
    setFailed(!result);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const more = (path: RoutePaths, label: string) => (
    <Button
      variant="ghost"
      size="sm"
      icon={<Icon name="chevronRight" size={16} />}
      onClick={() => navigate(path)}
    >
      {label}
    </Button>
  );

  const keyColumns: Array<Column<TopApiKey>> = [
    {
      key: "name",
      header: "Nøkkel",
      render: (k) => (
        <>
          <b>{k.name}</b>
          <div className={styles.muted}>{k.mosqueName || k.orgNr}</div>
        </>
      ),
    },
    {
      key: "last",
      header: "Sist brukt",
      render: (k) => formatDateTime(k.lastUsedAt),
    },
    {
      key: "requests",
      header: "Kall siste 30 dager",
      numeric: true,
      align: "right",
      render: (k) => nf(k.requests30Days),
    },
  ];

  const d = data;

  return (
    <>
      <PageHeader
        title="Oversikt"
        subtitle="Tall for hele iBønn: app-installasjoner, janazah-varsler, API-nøkler, TV-skjermer og moskeer."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={load}
            loading={isLoading}
          >
            Oppdater
          </Button>
        }
      />
      <SectionTabs />

      {failed && !d ? (
        <Card>
          <EmptyState icon="alert" title="Kunne ikke hente tallene">
            Prøv igjen med «Oppdater».
          </EmptyState>
        </Card>
      ) : isLoading && !d ? (
        <div className={styles.kpis}>
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={96} />
          ))}
        </div>
      ) : (
        <>
          <div className={styles.kpis}>
            <Stat
              strong
              label="Aktive app-installasjoner"
              value={d?.app.activeDevices}
              hint="siste 30 dager"
            />
            <Stat
              strong
              label="Følger janazah-varsler"
              value={d?.janazah.followers}
            />
            <Stat
              strong
              label="Aktive API-nøkler"
              value={d?.apiKeys.active}
              hint={d && `${nf(d.apiKeys.mosques)} moskeer`}
            />
            <Stat
              strong
              label="TV-er koblet til"
              value={d?.tv.active}
              hint={d && `${nf(d.tv.online)} på nå`}
            />
          </div>

          <div className={styles.grid}>
            <Card
              title="API-nøkler"
              actions={more(RoutePaths.ApiKeys, "Alle nøkler")}
            >
              <div className={styles.stats}>
                <Stat label="Utstedt totalt" value={d?.apiKeys.total} />
                <Stat label="Aktive" value={d?.apiKeys.active} />
                <Stat label="Trukket tilbake" value={d?.apiKeys.revoked} />
                <Stat
                  label="Brukt siste 7 dager"
                  value={d?.apiKeys.usedLast7Days}
                />
                <Stat
                  label="Kall siste 30 dager"
                  value={d?.apiKeys.requestsLast30Days}
                />
                <Stat label="Kall totalt" value={d?.apiKeys.requestsTotal} />
              </div>
              {d && d.apiKeys.top.length > 0 && (
                <>
                  <h3 className={styles.sub}>Mest brukt</h3>
                  <Table
                    columns={keyColumns}
                    rows={d.apiKeys.top}
                    rowKey={(k) => String(k.id)}
                  />
                </>
              )}
            </Card>

            <Card
              title="TV-skjermer"
              actions={more(RoutePaths.TvDevices, "Alle TV-er")}
            >
              <div className={styles.stats}>
                <Stat label="Koblet til" value={d?.tv.active} />
                <Stat label="På nå" value={d?.tv.online} hint="siste 15 min" />
                <Stat label="Moskeer med TV" value={d?.tv.mosques} />
                <Stat label="Venter på kode" value={d?.tv.pending} />
                <Stat label="Koden gikk ut" value={d?.tv.expired} />
                <Stat label="Koblet fra" value={d?.tv.revoked} />
              </div>
              {d && d.tv.byPlatform.length > 0 && (
                <>
                  <h3 className={styles.sub}>Tilkoblede TV-er</h3>
                  <Split items={d.tv.byPlatform} />
                  <Split
                    items={d.tv.byAppVersion}
                    format={(v) =>
                      v === "ukjent" ? "Ukjent versjon" : `v${v}`
                    }
                  />
                </>
              )}
            </Card>

            <Card
              title="Appen og janazah"
              actions={more(RoutePaths.MosqueStats, "Moské-statistikk")}
            >
              <div className={styles.stats}>
                <Stat
                  label="Aktive installasjoner"
                  value={d?.app.activeDevices}
                />
                <Stat
                  label="Moské-abonnementer"
                  value={d?.app.mosqueSubscriptions}
                />
                <Stat
                  label="Følger janazah-varsler"
                  value={d?.janazah.followers}
                />
                <Stat label="Kommende janazah" value={d?.janazah.upcoming} />
                <Stat
                  label="Nye dødsannonser"
                  value={d?.janazah.noticesLast30Days}
                  hint="siste 30 dager"
                />
                <Stat
                  label="Push fra moskeer"
                  value={d?.pushLast30Days}
                  hint="siste 30 dager"
                />
              </div>
              {d && (
                <>
                  <h3 className={styles.sub}>Installasjoner</h3>
                  <Split items={d.app.byPlatform} />
                  {d.janazah.followersByPlatform.length > 0 && (
                    <>
                      <h3 className={styles.sub}>Janazah-varsler</h3>
                      <Split items={d.janazah.followersByPlatform} />
                    </>
                  )}
                </>
              )}
              <p className={styles.muted}>
                Janazah-varsler telles fra app-versjonene som melder det fra, så
                tallet vokser etter hvert som brukerne oppdaterer appen.
              </p>
            </Card>

            <Card title="Appversjoner">
              {d?.app.appVersions && d.app.appVersions.length > 0 ? (
                <div className={styles.versionGrid}>
                  {d.app.appVersions.map((p) => (
                    <div key={p.platform}>
                      <h3 className={styles.sub}>
                        {platformLabel(p.platform)} · {nf(p.total)}
                      </h3>
                      <ul className={styles.versionList}>
                        {p.versions.map((v) => (
                          <li key={v.label}>
                            <span>
                              {v.label.startsWith("ukjent")
                                ? v.label
                                : `v${v.label}`}
                            </span>
                            <b>{nf(v.count)}</b>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.muted}>Ingen data ennå.</p>
              )}
              <p className={styles.muted}>
                Installasjoner sett siste 30 dager. Versjonen telles fra
                appversjonen som sender den (9.1); eldre versjoner vises som
                «ukjent». Se Firebase Analytics (App version) for fordelingen
                før det.
              </p>
            </Card>

            <Card
              title="Moskeer"
              actions={more(RoutePaths.MosqueAdmin, "Alle moskeer")}
            >
              <div className={styles.stats}>
                <Stat label="Moskeer" value={d?.mosques.total} />
                <Stat
                  label="Venter på godkjenning"
                  value={d?.mosques.awaitingApproval}
                />
                <Stat
                  label="Brukere med moské-tilgang"
                  value={d?.mosques.mosqueAdmins}
                />
              </div>
            </Card>
          </div>

          {d && (
            <span className={styles.muted}>
              Oppdatert {formatDateTime(d.generatedAt)}.
            </span>
          )}
        </>
      )}
    </>
  );
};

export default OverviewPage;
